import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { User, Mail, Calendar, Pencil, X, Loader2, Trash2, AlertTriangle, Camera } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { db } from "@/lib/db";

export function ProfilePage() {
  const { user, signOut, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Account deletion
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Avatar upload
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit mode values
  const [editDisplayName, setEditDisplayName] = useState("");

  const email = user?.email || "";
  const createdAt = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("de-DE", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "Unbekannt";

  // Current values from user metadata
  const displayName = user?.user_metadata?.display_name || "";
  const avatarUrl = user?.user_metadata?.avatar_url || null;

  const handleEdit = () => {
    setEditDisplayName(displayName);
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const uploadAvatar = async (): Promise<string | null> => {
    if (!avatarFile || !user?.id) return null;

    setUploadingAvatar(true);
    try {
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${user.id}/avatar.${fileExt}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, avatarFile, {
          upsert: true,
          contentType: avatarFile.type,
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL with cache-buster so re-uploads aren't served from browser cache
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      return `${publicUrl}?t=${Date.now()}`;
    } catch (err) {
      console.error('Avatar upload error:', err);
      setError('Fehler beim Hochladen des Profilbilds.');
      return null;
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      let newAvatarUrl = avatarUrl;

      // Upload avatar if selected
      if (avatarFile) {
        const uploadedUrl = await uploadAvatar();
        if (uploadedUrl) {
          newAvatarUrl = uploadedUrl;
        }
      }

      // Update user data
      const { error: updateError } = await supabase.auth.updateUser({
        data: { 
          display_name: editDisplayName,
          avatar_url: newAvatarUrl,
        },
      });

      if (updateError) {
        setError(updateError.message);
        setSaving(false);
        return;
      }

      // Update password if provided
      if (newPassword) {
        if (newPassword.length < 8) {
          setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
          setSaving(false);
          return;
        }
        if (newPassword !== confirmPassword) {
          setError("Die Passwörter stimmen nicht überein.");
          setSaving(false);
          return;
        }

        const { error: passwordError } = await supabase.auth.updateUser({
          password: newPassword,
        });

        if (passwordError) {
          setError(passwordError.message);
          setSaving(false);
          return;
        }
      }

      // Refresh user data to get updated metadata
      await refreshUser();
      setSuccess("Profil erfolgreich aktualisiert.");
      setIsEditing(false);
      setNewPassword("");
      setConfirmPassword("");
      setAvatarFile(null);
      setAvatarPreview(null);
      setEditDisplayName("");
    } catch {
      setError("Ein unerwarteter Fehler ist aufgetreten.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setSuccess(null);
    setAvatarFile(null);
    setAvatarPreview(null);
    setEditDisplayName("");
  };

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Bitte wähle ein Bild aus (JPG, PNG, GIF).');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Das Bild ist zu groß. Maximal 2 MB erlaubt.');
      return;
    }

    setAvatarFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "LÖSCHEN") return;
    setDeleting(true);
    setDeleteError(null);
    try {
      // 1. Delete all entries + favorites from local IndexedDB
      if (user?.id) {
        await db.practiceEntries.where("userId").equals(user.id).delete();
      }
      await db.favorites.clear();

      // 2. Delete practice_entries + auth user via RPC (SECURITY DEFINER)
      const { error: rpcError } = await supabase.rpc("delete_user");
      if (rpcError) throw rpcError;

      // 3. Sign out and redirect
      await signOut();
      navigate("/login");
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "Fehler beim Löschen des Accounts."
      );
      setDeleting(false);
    }
  };

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Avatar + Name */}
      <div className="flex flex-col items-center gap-3">
        <div className="relative">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#2563EB] via-[#3B82F6] to-[#06B6D4] overflow-hidden">
            {avatarUrl ? (
              <img 
                src={avatarUrl} 
                alt="Profilbild" 
                className="h-full w-full object-cover"
              />
            ) : (
              <User className="h-12 w-12 text-white" />
            )}
          </div>
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold">
            {displayName || "Praktizierender"}
          </h2>
          <p className="text-sm text-muted-foreground">{email}</p>
        </div>
      </div>

      {/* Status messages */}
      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md bg-emerald-500/10 px-4 py-3 text-sm text-emerald-400">
          {success}
        </div>
      )}

      {/* View Mode */}
      {!isEditing && (
        <>
          <div className="space-y-3">
            {/* Username Card */}
            <Card className="bg-gradient-to-br from-blue-50/50 to-cyan-50/50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-500/20">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/20">
                  <User className="h-5 w-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Anzeigename</p>
                  <p className="text-sm font-medium truncate">
                    {displayName || "Nicht festgelegt"}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card className="bg-gradient-to-br from-cyan-50/50 to-blue-50/50 dark:from-cyan-900/20 dark:to-blue-900/20 border-cyan-500/20">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan-500/20">
                  <Mail className="h-5 w-5 text-cyan-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">E-Mail</p>
                  <p className="text-sm font-medium truncate">{email}</p>
                </div>
              </CardContent>
            </Card>

            {/* Member since Card */}
            <Card className="bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-500/20">
              <CardContent className="py-4 flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500/20">
                  <Calendar className="h-5 w-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Mitglied seit</p>
                  <p className="text-sm font-medium">{createdAt}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Button
            variant="outline"
            className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
            onClick={handleEdit}
          >
            <Pencil className="mr-2 h-4 w-4" />
            Profil bearbeiten
          </Button>
        </>
      )}

      {/* Danger Zone */}
      {!isEditing && (
        <div className="pt-4 border-t border-red-500/20">
<Card className="border-red-500/20 bg-red-500/5">
            <CardContent className="py-4 flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500/10">
                <Trash2 className="h-5 w-5 text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Account löschen
                </p>
                <p className="text-xs text-gray-500 dark:text-muted-foreground mt-0.5">
                  Löscht alle deine Einträge und deinen Account unwiderruflich.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 border-red-500/40 text-red-500 hover:bg-red-500/10 hover:border-red-500/60"
                  onClick={() => { setShowDeleteModal(true); setDeleteConfirmText(""); setDeleteError(null); }}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Account löschen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Mode */}
      {isEditing && (
        <div className="space-y-4">
          {/* Avatar Upload */}
          <div className="space-y-2">
            <Label>Profilbild</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#2563EB] via-[#3B82F6] to-[#06B6D4] overflow-hidden">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Vorschau" className="h-full w-full object-cover" />
                ) : avatarUrl ? (
                  <img src={avatarUrl} alt="Profilbild" className="h-full w-full object-cover" />
                ) : (
                  <User className="h-8 w-8 text-white" />
                )}
              </div>
              <div className="flex-1">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarSelect}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="w-full"
                >
                  {uploadingAvatar ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Hochladen...</>
                  ) : (
                    <><Camera className="mr-2 h-4 w-4" />{avatarFile ? 'Bild ändern' : 'Bild auswählen'}</>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG oder GIF. Maximal 2 MB.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Anzeigename</Label>
            <Input
              id="displayName"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
              placeholder="Dein Name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="emailReadonly">E-Mail</Label>
            <Input
              id="emailReadonly"
              value={email}
              disabled
              className="opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              Die E-Mail-Adresse kann nicht geändert werden.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">Neues Passwort (optional)</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Neues Passwort"
              autoComplete="new-password"
            />
          </div>

          {newPassword && (
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                autoComplete="new-password"
              />
            </div>
          )}

          <div className="flex gap-3">
            <Button
              className="flex-1 bg-vajra-blue hover:bg-vajra-blue/90"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                "Speichern"
              )}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleCancel}
              disabled={saving}
            >
              <X className="mr-2 h-4 w-4" />
              Abbrechen
            </Button>
          </div>
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm"
          onClick={() => !deleting && setShowDeleteModal(false)}
        >
          <div
            className="w-full max-w-sm bg-white dark:bg-[#0D1117] rounded-2xl p-6 shadow-2xl border border-red-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 bg-red-500/10 rounded-xl">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Account löschen
              </h3>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Diese Aktion ist <strong className="text-red-500">unwiderruflich</strong>. Alle deine Einträge, Favoriten und Kontodaten werden dauerhaft gelöscht.
            </p>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Tippe <strong className="font-mono text-red-500">LÖSCHEN</strong> zum Bestätigen:
            </p>

            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="LÖSCHEN"
              className="mb-3 border-red-500/30 focus:border-red-500/60 font-mono"
              disabled={deleting}
              autoComplete="off"
            />

            {deleteError && (
              <p className="text-xs text-red-500 mb-3">{deleteError}</p>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Abbrechen
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white border-0"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== "LÖSCHEN" || deleting}
              >
                {deleting ? (
                  <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />löschen...</>
                ) : (
                  <><Trash2 className="mr-1.5 h-4 w-4" />Endgültig löschen</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
